import BaseModal from "@/components/shared/BaseModal";
import { Box } from "@/components/ui/box";
import { Input, InputField } from "@/components/ui/input";
import { Text } from "@/components/ui/text";
import { formatPhoneNumber, getPhoneDigits, getPhoneFormat } from "@/utils/formatPhoneNumber";
import { useT } from "@transifex/react";
import type React from "react";
import { useState } from "react";

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone: string): boolean => {
  return getPhoneFormat().length === getPhoneDigits(phone).length;
};

interface ContactModalProps {
  isOpen: boolean;
  mode: "email" | "phone";
  handleClose: () => void;
  handleConfirm: (contact: string) => void;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, mode, handleClose, handleConfirm }) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [formatPhoneNumberState, setFormatPhoneNumberState] = useState(formatPhoneNumber(""));
  const [loading, setLoading] = useState(false);
  const t = useT();

  const onConfirm = () => {
    if (mode === "email") {
      if (!validateEmail(value)) {
        setError(t("Invalid email format"));
        return;
      }
    } else if (!validatePhone(value)) {
      setError(t("Invalid phone number"));
      return;
    }
    setError("");
    setLoading(true);
    handleConfirm(value);
    setValue("");
    setLoading(false);
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === "email" ? t("Send Receipt via Email") : t("Send Receipt via Text")}
      subtitle={mode === "email" ? t("Please enter your email.") : t("Please enter your phone number.")}
      buttons={[{ title: "Send receipt", onPress: onConfirm, loading: loading }]}
      noIcon={false}
    >
      <Box className="gap-[10px]">
        <Text className="text-typography-900 text-[14px] font-[600] leading-[20px] align-middle">{mode === "email" ? t("Email") : t("Phone number")}</Text>

        <Box>
          <Input
            className={`h-[52px] pt-[8px] pb-[8px] pl-[8px] gap-[8px] rounded-[6px] border-[1px] ${error ? "bg-error-50 border-error-400" : "bg-background-50 border-outline-300"}`}
          >
            <InputField
              placeholder={mode === "email" ? "email@example.com" : "+1 (123) 456-7890"}
              value={mode === "phone" ? formatPhoneNumberState : value}
              onChangeText={(text) => {
                if (mode === "phone") {
                  const numbersOnly = getPhoneDigits(text);
                  const formattedNumber = formatPhoneNumber(numbersOnly);
                  setFormatPhoneNumberState(formattedNumber);
                  setValue(numbersOnly);
                } else {
                  setValue(text);
                }
                setError("");
              }}
              keyboardType={mode === "email" ? "email-address" : "phone-pad"}
              autoCapitalize={mode === "email" ? "none" : "sentences"}
              className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
            />
          </Input>
          {error && <Text className="text-error-100 text-[14px] leading-[20px] pt-1">{error}</Text>}
        </Box>
      </Box>
    </BaseModal>
  );
};

export default ContactModal;
